import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

import type { UseComboboxState, UseSelectState } from 'downshift';

import { getValue } from '../utils/option';

type IntrinsicSelect = JSX.IntrinsicElements['select'];

export type SingleValue = string | number;

export type Value = SingleValue | SingleValue[] | undefined;

type DownshiftChanges<T> =
  | Partial<UseComboboxState<T>>
  | Partial<UseSelectState<T>>;

export type ChangeEvent = {
  value?: SingleValue;
  checked: boolean;
};

export interface MultiProps extends Omit<
  IntrinsicSelect,
  'value' | 'onChange'
> {
  /** The value of the combobox when used in a controlled state */
  value?: Value;
  /** A callback function containing the value of the last selected option */
  onChange?: (event: ChangeEvent) => void;
  /** Whether to accept multiple values or a single value */
  multi?: boolean;
  /** Whether to accept typed in string as a value */
  withFreeForm?: boolean;
}

export interface MultiReturn<T> {
  /** The current value of the component */
  value: Value;
  /** Whether the component has a valid value */
  hasValue: boolean;
  /**
   * A callback function that is passed to downshift when the selected item
   * changes
   */
  handleChange: (changes: DownshiftChanges<T>) => void;
  /**
   * A function that is used to remove an element from the list of selected
   * values
   */
  removeValue: (value: SingleValue) => void;
  /** Compare an option to the current value to see whether or not it is selected */
  getIsSelected: (option: ReactNode) => boolean;
}

const EMPTY_SINGLE_VALUE: Value = undefined;
const EMPTY_MULTI_VALUE: Value = [];

export function useMultiSelect<T extends ReactNode>(
  props: MultiProps
): MultiReturn<T> {
  const [value, setValue] = useState(
    props.value
      ? props.value
      : props.multi
        ? EMPTY_MULTI_VALUE
        : EMPTY_SINGLE_VALUE
  );

  useEffect(() => {
    if (typeof props.value !== 'undefined') {
      setValue(props.value);
    }
  }, [props.value]);

  const handleChange = (changes: DownshiftChanges<T>): void => {
    const selectedValue = getValue(changes.selectedItem);

    if (
      typeof selectedValue === 'undefined' ||
      !(Array.isArray(value) && props.multi)
    ) {
      setValue(selectedValue);
      props.onChange?.({ value: selectedValue, checked: true });
      return;
    }

    const nextValues = value.includes(selectedValue)
      ? value.filter(val => val !== selectedValue)
      : [...value, selectedValue];
    setValue(nextValues);
    props.onChange?.({ value: selectedValue, checked: true });
  };

  const removeValue = (valueToRemove: SingleValue): void => {
    if (Array.isArray(value) && props.multi) {
      const nextValue = [...value].filter(
        (val: Value) => val !== valueToRemove
      );
      setValue(nextValue);
      props.onChange?.({ value: valueToRemove, checked: false });
    }
  };

  const getIsSelected = (option: ReactNode): boolean => {
    if (Array.isArray(value) && props.multi) {
      return (
        value.findIndex((val: SingleValue) => getValue(option) === val) >= 0
      );
    }

    return getValue(option) === value;
  };

  const getHasValue = (): boolean => {
    if (Array.isArray(value) && props.multi) {
      return value.length > 0;
    }

    return typeof value !== 'undefined';
  };

  return {
    value,
    hasValue: getHasValue(),
    getIsSelected,
    handleChange,
    removeValue,
  };
}
